import { useEffect } from 'react';

// ============================================================
// PRODUCT CATALOG — single source of truth for tracking values
// All prices in US Dollars (USD).
// ============================================================
export type ProductKey = 'ff365';
type Product = {
  id: string;
  name: string;
  value: number;
  currency: 'USD';
  checkoutUrl: string;
};
export const PRODUCTS: Record<ProductKey, Product> = {
  ff365: {
    id: 'functional-fitness-365',
    name: 'Functional Fitness 365',
    value: 17,
    currency: 'USD',
    checkoutUrl: 'https://functional-fitness-365.impultienda.ar/checkout'
  }
};
export const PIXEL_ID = '1119692460632479';
type FbqFunction = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  push: FbqFunction;
  loaded: boolean;
  version: string;
};
declare global {
  interface Window {
    fbq?: FbqFunction;
    _fbq?: FbqFunction;
  }
}
let initialized = false;
let viewContentSent = false;
let lastCheckoutTime = 0;

// ============================================================
// Pixel bootstrap — the official Meta snippet, typed and made
// idempotent so it never double-inits (StrictMode, HMR, re-mounts).
// ============================================================
function ensurePixel(): FbqFunction | null {
  if (typeof window === 'undefined') return null;
  if (!window.fbq) {
    const n = function (...args: unknown[]) {
      if (n.callMethod) n.callMethod.apply(n, args);else n.queue.push(args);
    } as FbqFunction;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    window.fbq = n;
    if (!window._fbq) window._fbq = n;
    if (!document.getElementById('fb-pixel-script')) {
      const script = document.createElement('script');
      script.id = 'fb-pixel-script';
      script.async = true;
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(script);
    }
  }
  if (!initialized) {
    window.fbq('init', PIXEL_ID);
    window.fbq('track', 'PageView');
    initialized = true;
  }
  return window.fbq;
}
const eventId = (name: string) => `${name}.${Date.now()}.${Math.random().toString(36).slice(2, 10)}`;
const productPayload = (p: Product, value: number) => ({
  value,
  currency: p.currency,
  content_ids: [p.id],
  content_name: p.name,
  content_type: 'product',
  contents: [{
    id: p.id,
    quantity: 1,
    item_price: value
  }],
  num_items: 1
});

// ============================================================
// Tracking helpers — use these from anywhere in the app
// ============================================================
export const trackViewContent = (productKey: ProductKey) => {
  if (viewContentSent) return;
  const fbq = ensurePixel();
  if (!fbq) return;
  viewContentSent = true;
  const p = PRODUCTS[productKey];
  fbq('track', 'ViewContent', productPayload(p, p.value), {
    eventID: eventId('ViewContent')
  });
};
export const trackInitiateCheckout = (productKey: ProductKey) => {
  const now = Date.now();
  if (now - lastCheckoutTime < 2000) return; // debounce double taps
  lastCheckoutTime = now;
  const fbq = ensurePixel();
  if (!fbq) return;
  const p = PRODUCTS[productKey];
  fbq('track', 'InitiateCheckout', productPayload(p, p.value), {
    eventID: eventId('InitiateCheckout')
  });
};
export const trackPurchase = (productKey: ProductKey, overrideValue?: number) => {
  const fbq = ensurePixel();
  if (!fbq) return;
  const p = PRODUCTS[productKey];
  const value = typeof overrideValue === 'number' && Number.isFinite(overrideValue) ? overrideValue : p.value;
  fbq('track', 'Purchase', productPayload(p, value), {
    eventID: eventId('Purchase')
  });
};

/**
 * Navigates once the pixel has had a moment to send queued events.
 * If the Meta script is still loading, waits a little longer (max ~900ms)
 * so the InitiateCheckout event isn't lost on page unload.
 */
export const navigateAfterTracking = (href: string) => {
  if (typeof window === 'undefined') return;
  const started = Date.now();
  const go = () => {
    const loaded = Boolean(window.fbq?.callMethod);
    if (loaded || Date.now() - started > 900) {
      window.setTimeout(() => window.location.assign(href), loaded ? 180 : 0);
      return;
    }
    window.setTimeout(go, 100);
  };
  go();
};

// ============================================================
// MetaPixel component — initializes the pixel and handles
// the Purchase return URL (?purchase=1[&value=...])
// ============================================================
export function MetaPixel() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      ensurePixel();
    } catch {
      // Tracking must never break the landing page.
    }

    // Fire ViewContent when the offer is actually seen
    const pricing = document.getElementById('pricing');
    let observer: IntersectionObserver | null = null;
    if (pricing && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          trackViewContent('ff365');
          observer?.disconnect();
        }
      }, {
        threshold: 0.25
      });
      observer.observe(pricing);
    }

    // e.g. https://landing.com/?purchase=1          → fires Purchase
    // e.g. https://landing.com/?purchase=1&value=17 → overrides the value
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('purchase')) {
        const valueParam = params.get('value');
        const overrideValue = valueParam ? Number(valueParam) : undefined;
        trackPurchase('ff365', overrideValue);
        const url = new URL(window.location.href);
        url.searchParams.delete('purchase');
        url.searchParams.delete('value');
        window.history.replaceState(window.history.state, '', url.toString());
      }
    } catch {
      // Tracking must never break the landing page.
    }
    return () => observer?.disconnect();
  }, []);
  return null;
}